import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    TextField,
    Stack,
    Paper,
    InputAdornment,
    Grid,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Select,
    MenuItem,
    FormControl,
    Tabs,
    Tab,
    Menu,
    Divider
} from '@mui/material';
import {
    Add as AddIcon,
    Close as CloseIcon,
    Search as SearchIcon,
    FileDownload as FileDownloadIcon,
    CheckCircle as CheckCircleIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import useIndustries from '../hooks/useIndustries';
import useSectors from '../hooks/useSectors';
import useCountries from '../hooks/useCountries';

// Define filter categories
const filterCategories = [
    {
        id: 'price',
        label: 'Price & Averages',
        filters: [
            { id: 'price', label: 'Price', type: 'range', hasPresetRanges: true },
            { id: 'averageComparison', label: 'Moving Average', type: 'composite' },
            { id: 'changesPercentage', label: 'Change %', type: 'range', hasPresetRanges: true },
            { id: 'change', label: 'Change Amount', type: 'range', hasPresetRanges: true },
            { id: 'dayLow', label: 'Day Low', type: 'range', hasPresetRanges: true },
            { id: 'dayHigh', label: 'Day High', type: 'range', hasPresetRanges: true },
            { id: 'yearLow', label: 'Year Low', type: 'range', hasPresetRanges: true },
            { id: 'yearHigh', label: 'Year High', type: 'range', hasPresetRanges: true },
            { id: 'open', label: 'Open', type: 'range', hasPresetRanges: true },
            { id: 'previousClose', label: 'Previous Close', type: 'range', hasPresetRanges: true },
        ]
    },
    {
        id: 'fundamentals',
        label: 'Fundamentals',
        filters: [
            { id: 'marketCap', label: 'Market Cap', type: 'range', hasPresetRanges: true },
            { id: 'rsi', label: 'RSI', type: 'range', hasPresetRanges: true },
            { id: 'volume', label: 'Volume', type: 'range', hasPresetRanges: true },
            { id: 'avgVolume', label: 'Average Volume', type: 'range', hasPresetRanges: true },
            { id: 'sharesOutstanding', label: 'Shares Outstanding', type: 'range', hasPresetRanges: true },
            { id: 'eps', label: 'EPS', type: 'range', hasPresetRanges: true },
            { id: 'pe', label: 'PE Ratio', type: 'range', hasPresetRanges: true },
            { id: 'beta', label: 'Beta', type: 'range', hasPresetRanges: true },
            { id: 'dcf', label: 'DCF', type: 'range', hasPresetRanges: true },
            { id: 'dcfDiff', label: 'DCF Diff', type: 'range', hasPresetRanges: true },
        ]
    },
    {
        id: 'company',
        label: 'Company',
        filters: [
            { id: 'exchange', label: 'Exchange', type: 'select' },
            { id: 'sector', label: 'Sector', type: 'select' },
            { id: 'industry', label: 'Industry', type: 'select' },
            { id: 'country', label: 'Country', type: 'select' },
            { id: 'isEtf', label: 'ETF', type: 'boolean' },
        ]
    }
];

const predefinedRanges = {
    price: {
        above: [
            { label: 'Above $1', value: 1 },
            { label: 'Above $5', value: 5 },
            { label: 'Above $10', value: 10 },
            { label: 'Above $20', value: 20 },
            { label: 'Above $50', value: 50 },
            { label: 'Above $100', value: 100 },
            { label: 'Above $200', value: 200 },
            { label: 'Above $500', value: 500 },
            { label: 'Above $1000', value: 1000 }
        ],
        below: [
            { label: 'Under $1', value: 1 },
            { label: 'Under $5', value: 5 },
            { label: 'Under $10', value: 10 },
            { label: 'Under $20', value: 20 },
            { label: 'Under $50', value: 50 },
            { label: 'Under $100', value: 100 },
            { label: 'Under $200', value: 200 },
            { label: 'Under $500', value: 500 },
            { label: 'Under $1000', value: 1000 }
        ],
        between: [
            { label: 'Between $1 - $5', min: 1, max: 5 },
            { label: 'Between $5 - $10', min: 5, max: 10 },
            { label: 'Between $10 - $20', min: 10, max: 20 },
            { label: 'Between $20 - $50', min: 20, max: 50 },
            { label: 'Between $50 - $100', min: 50, max: 100 },
            { label: 'Between $100 - $200', min: 100, max: 200 },
            { label: 'Between $200 - $500', min: 200, max: 500 },
            { label: 'Between $500 - $1000', min: 500, max: 1000 }
        ]
    },
    marketCap: {
        above: [
            { label: 'Above 1M', value: 1000000 },
            { label: 'Above 10M', value: 10000000 },
            { label: 'Above 100M', value: 100000000 },
            { label: 'Above 500M', value: 500000000 },
            { label: 'Above 1B', value: 1000000000 },
            { label: 'Above 10B', value: 10000000000 },
            { label: 'Above 50B', value: 50000000000 },
            { label: 'Above 100B', value: 100000000000 },
            { label: 'Above 500B', value: 500000000000 },
            { label: 'Above 1T', value: 1000000000000 }
        ],
        below: [
            { label: 'Under 1M', value: 1000000 },
            { label: 'Under 10M', value: 10000000 },
            { label: 'Under 100M', value: 100000000 },
            { label: 'Under 500M', value: 500000000 },
            { label: 'Under 1B', value: 1000000000 },
            { label: 'Under 10B', value: 10000000000 },
            { label: 'Under 50B', value: 50000000000 },
            { label: 'Under 100B', value: 100000000000 },
            { label: 'Under 500B', value: 500000000000 },
            { label: 'Under 1T', value: 1000000000000 }
        ],
        between: [
            { label: 'Between 1M - 10M', min: 1000000, max: 10000000 },
            { label: 'Between 10M - 100M', min: 10000000, max: 100000000 },
            { label: 'Between 100M - 500M', min: 100000000, max: 500000000 },
            { label: 'Between 500M - 1B', min: 500000000, max: 1000000000 },
            { label: 'Between 1B - 10B', min: 1000000000, max: 10000000000 },
            { label: 'Between 10B - 50B', min: 10000000000, max: 50000000000 },
            { label: 'Between 50B - 100B', min: 50000000000, max: 100000000000 },
            { label: 'Between 100B - 500B', min: 100000000000, max: 500000000000 },
            { label: 'Between 500B - 1T', min: 500000000000, max: 1000000000000 }
        ]
    },
    changesPercentage: {
        above: [
            { label: 'Above 1%', value: 1 },
            { label: 'Above 5%', value: 5 },
            { label: 'Above 10%', value: 10 },
            { label: 'Above 20%', value: 20 },
            { label: 'Above 50%', value: 50 }
        ],
        below: [
            { label: 'Under -1%', value: -1 },
            { label: 'Under -5%', value: -5 },
            { label: 'Under -10%', value: -10 },
            { label: 'Under -20%', value: -20 },
            { label: 'Under -50%', value: -50 }
        ],
        between: [
            { label: 'Between -5% and 5%', min: -5, max: 5 },
            { label: 'Between -10% and 10%', min: -10, max: 10 },
            { label: 'Between -20% and 20%', min: -20, max: 20 },
            { label: 'Between 0% and 10%', min: 0, max: 10 },
            { label: 'Between 0% and 20%', min: 0, max: 20 }
        ]
    },
    volume: {
        above: [
            { label: 'Above 100K', value: 100000 },
            { label: 'Above 500K', value: 500000 },
            { label: 'Above 1M', value: 1000000 },
            { label: 'Above 5M', value: 5000000 },
            { label: 'Above 10M', value: 10000000 },
            { label: 'Above 50M', value: 50000000 },
            { label: 'Above 100M', value: 100000000 }
        ],
        below: [
            { label: 'Under 100K', value: 100000 },
            { label: 'Under 500K', value: 500000 },
            { label: 'Under 1M', value: 1000000 },
            { label: 'Under 5M', value: 5000000 },
            { label: 'Under 10M', value: 10000000 },
            { label: 'Under 50M', value: 50000000 },
            { label: 'Under 100M', value: 100000000 }
        ],
        between: [
            { label: 'Between 100K - 1M', min: 100000, max: 1000000 },
            { label: 'Between 1M - 10M', min: 1000000, max: 10000000 },
            { label: 'Between 10M - 100M', min: 10000000, max: 100000000 }
        ]
    },
    avgVolume: {
        above: [
            { label: 'Above 100K', value: 100000 },
            { label: 'Above 500K', value: 500000 },
            { label: 'Above 1M', value: 1000000 },
            { label: 'Above 5M', value: 5000000 },
            { label: 'Above 10M', value: 10000000 },
            { label: 'Above 50M', value: 50000000 },
            { label: 'Above 100M', value: 100000000 }
        ],
        below: [
            { label: 'Under 100K', value: 100000 },
            { label: 'Under 500K', value: 500000 },
            { label: 'Under 1M', value: 1000000 },
            { label: 'Under 5M', value: 5000000 },
            { label: 'Under 10M', value: 10000000 },
            { label: 'Under 50M', value: 50000000 },
            { label: 'Under 100M', value: 100000000 }
        ],
        between: [
            { label: 'Between 100K - 1M', min: 100000, max: 1000000 },
            { label: 'Between 1M - 10M', min: 1000000, max: 10000000 },
            { label: 'Between 10M - 100M', min: 10000000, max: 100000000 }
        ]
    },
    beta: {
        above: [
            { label: 'Above 0.5', value: 0.5 },
            { label: 'Above 1.0', value: 1.0 },
            { label: 'Above 1.5', value: 1.5 },
            { label: 'Above 2.0', value: 2.0 },
            { label: 'Above 3.0', value: 3.0 }
        ],
        below: [
            { label: 'Under 0.5', value: 0.5 },
            { label: 'Under 1.0', value: 1.0 },
            { label: 'Under 1.5', value: 1.5 },
            { label: 'Under 2.0', value: 2.0 },
            { label: 'Under 3.0', value: 3.0 }
        ],
        between: [
            { label: 'Between 0 - 1', min: 0, max: 1 },
            { label: 'Between 1 - 2', min: 1, max: 2 },
            { label: 'Between 2 - 3', min: 2, max: 3 }
        ]
    },
    pe: {
        above: [
            { label: 'Above 5', value: 5 },
            { label: 'Above 10', value: 10 },
            { label: 'Above 15', value: 15 },
            { label: 'Above 20', value: 20 },
            { label: 'Above 30', value: 30 },
            { label: 'Above 50', value: 50 },
            { label: 'Above 100', value: 100 }
        ],
        below: [
            { label: 'Under 5', value: 5 },
            { label: 'Under 10', value: 10 },
            { label: 'Under 15', value: 15 },
            { label: 'Under 20', value: 20 },
            { label: 'Under 30', value: 30 },
            { label: 'Under 50', value: 50 },
            { label: 'Under 100', value: 100 }
        ],
        between: [
            { label: 'Between 0 - 10', min: 0, max: 10 },
            { label: 'Between 10 - 20', min: 10, max: 20 },
            { label: 'Between 20 - 50', min: 20, max: 50 }
        ]
    },
    eps: {
        above: [
            { label: 'Above 0', value: 0 },
            { label: 'Above 1', value: 1 },
            { label: 'Above 2', value: 2 },
            { label: 'Above 5', value: 5 },
            { label: 'Above 10', value: 10 }
        ],
        below: [
            { label: 'Under 0', value: 0 },
            { label: 'Under 1', value: 1 },
            { label: 'Under 2', value: 2 },
            { label: 'Under 5', value: 5 },
            { label: 'Under 10', value: 10 }
        ],
        between: [
            { label: 'Between 0 - 1', min: 0, max: 1 },
            { label: 'Between 1 - 5', min: 1, max: 5 },
            { label: 'Between 5 - 10', min: 5, max: 10 }
        ]
    },
    change: {
        above: [
            { label: 'Above $1', value: 1 },
            { label: 'Above $5', value: 5 },
            { label: 'Above $10', value: 10 },
            { label: 'Above $20', value: 20 },
            { label: 'Above $50', value: 50 },
            { label: 'Above $100', value: 100 },
            { label: 'Above $200', value: 200 },
            { label: 'Above $500', value: 500 },
        ],
        below: [
            { label: 'Under $1', value: 1 },
            { label: 'Under $5', value: 5 },
            { label: 'Under $10', value: 10 },
            { label: 'Under $20', value: 20 },
            { label: 'Under $50', value: 50 },
            { label: 'Under $100', value: 100 },
            { label: 'Under $200', value: 200 },
            { label: 'Under $500', value: 500 },
        ],
        between: [
            { label: 'Between $1 - $5', min: 1, max: 5 },
            { label: 'Between $5 - $10', min: 5, max: 10 },
            { label: 'Between $10 - $20', min: 10, max: 20 },
            { label: 'Between $20 - $50', min: 20, max: 50 },
            { label: 'Between $50 - $100', min: 50, max: 100 },
            { label: 'Between $100 - $200', min: 100, max: 200 },
        ]
    },
    dcf: {
        above: [
            { label: 'Above $10', value: 10 },
            { label: 'Above $20', value: 20 },
            { label: 'Above $50', value: 50 },
            { label: 'Above $100', value: 100 },
            { label: 'Above $200', value: 200 },
        ],
        below: [
            { label: 'Under $10', value: 10 },
            { label: 'Under $20', value: 20 },
            { label: 'Under $50', value: 50 },
            { label: 'Under $100', value: 100 },
            { label: 'Under $200', value: 200 },
        ],
        between: [
            { label: 'Between $10 - $50', min: 10, max: 50 },
            { label: 'Between $50 - $100', min: 50, max: 100 },
            { label: 'Between $100 - $200', min: 100, max: 200 },
        ]
    },
    dcfDiff: {
        above: [
            { label: 'Above 10%', value: 10 },
            { label: 'Above 20%', value: 20 },
            { label: 'Above 30%', value: 30 },
            { label: 'Above 50%', value: 50 },
            { label: 'Above 100%', value: 100 },
        ],
        below: [
            { label: 'Under -10%', value: -10 },
            { label: 'Under -20%', value: -20 },
            { label: 'Under -30%', value: -30 },
            { label: 'Under -50%', value: -50 },
            { label: 'Under -100%', value: -100 },
        ],
        between: [
            { label: 'Between -20% and 20%', min: -20, max: 20 },
            { label: 'Between -50% and 50%', min: -50, max: 50 },
            { label: 'Between 0% and 50%', min: 0, max: 50 },
        ]
    },
    dayLow: {
        above: [
            { label: 'Above $1', value: 1 },
            { label: 'Above $5', value: 5 },
            { label: 'Above $10', value: 10 },
            { label: 'Above $20', value: 20 },
            { label: 'Above $50', value: 50 },
            { label: 'Above $100', value: 100 },
            { label: 'Above $200', value: 200 },
            { label: 'Above $500', value: 500 },
        ],
        below: [
            { label: 'Under $1', value: 1 },
            { label: 'Under $5', value: 5 },
            { label: 'Under $10', value: 10 },
            { label: 'Under $20', value: 20 },
            { label: 'Under $50', value: 50 },
            { label: 'Under $100', value: 100 },
            { label: 'Under $200', value: 200 },
            { label: 'Under $500', value: 500 },
        ],
        between: [
            { label: 'Between $1 - $5', min: 1, max: 5 },
            { label: 'Between $5 - $10', min: 5, max: 10 },
            { label: 'Between $10 - $20', min: 10, max: 20 },
            { label: 'Between $20 - $50', min: 20, max: 50 },
            { label: 'Between $50 - $100', min: 50, max: 100 },
        ]
    },
    dayHigh: {
        above: [
            { label: 'Above $1', value: 1 },
            { label: 'Above $5', value: 5 },
            { label: 'Above $10', value: 10 },
            { label: 'Above $20', value: 20 },
            { label: 'Above $50', value: 50 },
            { label: 'Above $100', value: 100 },
            { label: 'Above $200', value: 200 },
            { label: 'Above $500', value: 500 },
        ],
        below: [
            { label: 'Under $1', value: 1 },
            { label: 'Under $5', value: 5 },
            { label: 'Under $10', value: 10 },
            { label: 'Under $20', value: 20 },
            { label: 'Under $50', value: 50 },
            { label: 'Under $100', value: 100 },
            { label: 'Under $200', value: 200 },
            { label: 'Under $500', value: 500 },
        ],
        between: [
            { label: 'Between $1 - $5', min: 1, max: 5 },
            { label: 'Between $5 - $10', min: 5, max: 10 },
            { label: 'Between $10 - $20', min: 10, max: 20 },
            { label: 'Between $20 - $50', min: 20, max: 50 },
            { label: 'Between $50 - $100', min: 50, max: 100 },
            { label: 'Between $100 - $200', min: 100, max: 200 },
        ]
    },
    yearLow: {
        above: [
            { label: 'Above $1', value: 1 },
            { label: 'Above $5', value: 5 },
            { label: 'Above $10', value: 10 },
            { label: 'Above $20', value: 20 },
            { label: 'Above $50', value: 50 },
            { label: 'Above $100', value: 100 },
            { label: 'Above $200', value: 200 },
            { label: 'Above $500', value: 500 },
        ],
        below: [
            { label: 'Under $1', value: 1 },
            { label: 'Under $5', value: 5 },
            { label: 'Under $10', value: 10 },
            { label: 'Under $20', value: 20 },
            { label: 'Under $50', value: 50 },
            { label: 'Under $100', value: 100 },
            { label: 'Under $200', value: 200 },
            { label: 'Under $500', value: 500 },
        ],
        between: [
            { label: 'Between $1 - $5', min: 1, max: 5 },
            { label: 'Between $5 - $10', min: 5, max: 10 },
            { label: 'Between $10 - $20', min: 10, max: 20 },
            { label: 'Between $20 - $50', min: 20, max: 50 },
            { label: 'Between $50 - $100', min: 50, max: 100 },
            { label: 'Between $100 - $200', min: 100, max: 200 },
        ]
    },
    yearHigh: {
        above: [
            { label: 'Above $1', value: 1 },
            { label: 'Above $5', value: 5 },
            { label: 'Above $10', value: 10 },
            { label: 'Above $20', value: 20 },
            { label: 'Above $50', value: 50 },
            { label: 'Above $100', value: 100 },
            { label: 'Above $200', value: 200 },
            { label: 'Above $500', value: 500 },
        ],
        below: [
            { label: 'Under $1', value: 1 },
            { label: 'Under $5', value: 5 },
            { label: 'Under $10', value: 10 },
            { label: 'Under $20', value: 20 },
            { label: 'Under $50', value: 50 },
            { label: 'Under $100', value: 100 },
            { label: 'Under $200', value: 200 },
            { label: 'Under $500', value: 500 },
        ],
        between: [
            { label: 'Between $1 - $5', min: 1, max: 5 },
            { label: 'Between $5 - $10', min: 5, max: 10 },
            { label: 'Between $10 - $20', min: 10, max: 20 },
            { label: 'Between $20 - $50', min: 20, max: 50 },
            { label: 'Between $50 - $100', min: 50, max: 100 },
            { label: 'Between $100 - $200', min: 100, max: 200 },
        ]
    },
    open: {
        above: [
            { label: 'Above $1', value: 1 },
            { label: 'Above $5', value: 5 },
            { label: 'Above $10', value: 10 },
            { label: 'Above $20', value: 20 },
            { label: 'Above $50', value: 50 },
            { label: 'Above $100', value: 100 },
            { label: 'Above $200', value: 200 },
            { label: 'Above $500', value: 500 },
        ],
        below: [
            { label: 'Under $1', value: 1 },
            { label: 'Under $5', value: 5 },
            { label: 'Under $10', value: 10 },
            { label: 'Under $20', value: 20 },
            { label: 'Under $50', value: 50 },
            { label: 'Under $100', value: 100 },
            { label: 'Under $200', value: 200 },
            { label: 'Under $500', value: 500 },
        ],
        between: [
            { label: 'Between $1 - $5', min: 1, max: 5 },
            { label: 'Between $5 - $10', min: 5, max: 10 },
            { label: 'Between $10 - $20', min: 10, max: 20 },
            { label: 'Between $20 - $50', min: 20, max: 50 },
            { label: 'Between $50 - $100', min: 50, max: 100 },
            { label: 'Between $100 - $200', min: 100, max: 200 },
        ]
    },
    previousClose: {
        above: [
            { label: 'Above $1', value: 1 },
            { label: 'Above $5', value: 5 },
            { label: 'Above $10', value: 10 },
            { label: 'Above $20', value: 20 },
            { label: 'Above $50', value: 50 },
            { label: 'Above $100', value: 100 },
            { label: 'Above $200', value: 200 },
            { label: 'Above $500', value: 500 },
        ],
        below: [
            { label: 'Under $1', value: 1 },
            { label: 'Under $5', value: 5 },
            { label: 'Under $10', value: 10 },
            { label: 'Under $20', value: 20 },
            { label: 'Under $50', value: 50 },
            { label: 'Under $100', value: 100 },
            { label: 'Under $200', value: 200 },
            { label: 'Under $500', value: 500 },
        ],
        between: [
            { label: 'Between $1 - $5', min: 1, max: 5 },
            { label: 'Between $5 - $10', min: 5, max: 10 },
            { label: 'Between $10 - $20', min: 10, max: 20 },
            { label: 'Between $20 - $50', min: 20, max: 50 },
            { label: 'Between $50 - $100', min: 50, max: 100 },
            { label: 'Between $100 - $200', min: 100, max: 200 },
        ]
    },
    sharesOutstanding: {
        above: [
            { label: 'Above 1M', value: 1000000 },
            { label: 'Above 10M', value: 10000000 },
            { label: 'Above 100M', value: 100000000 },
            { label: 'Above 500M', value: 500000000 },
            { label: 'Above 1B', value: 1000000000 },
            { label: 'Above 5B', value: 5000000000 },
            { label: 'Above 10B', value: 10000000000 },
        ],
        below: [
            { label: 'Under 1M', value: 1000000 },
            { label: 'Under 10M', value: 10000000 },
            { label: 'Under 100M', value: 100000000 },
            { label: 'Under 500M', value: 500000000 },
            { label: 'Under 1B', value: 1000000000 },
            { label: 'Under 5B', value: 5000000000 },
            { label: 'Under 10B', value: 10000000000 },
        ],
        between: [
            { label: 'Between 1M - 10M', min: 1000000, max: 10000000 },
            { label: 'Between 10M - 100M', min: 10000000, max: 100000000 },
            { label: 'Between 100M - 500M', min: 100000000, max: 500000000 },
            { label: 'Between 500M - 1B', min: 500000000, max: 1000000000 },
            { label: 'Between 1B - 10B', min: 1000000000, max: 10000000000 },
        ]
    },
    rsi: {
        above: [
            { label: 'Above 30', value: 30 },
            { label: 'Above 50', value: 50 },
            { label: 'Above 70', value: 70 },
            { label: 'Above 80', value: 80 },
            { label: 'Above 90', value: 90 }
        ],
        below: [
            { label: 'Under 10', value: 10 },
            { label: 'Under 20', value: 20 },
            { label: 'Under 30', value: 30 },
            { label: 'Under 50', value: 50 },
            { label: 'Under 70', value: 70 }
        ],
        between: [
            { label: 'Between 20 - 80', min: 20, max: 80 },
            { label: 'Between 30 - 70', min: 30, max: 70 },
            { label: 'Between 40 - 60', min: 40, max: 60 },
            { label: 'Between 0 - 30', min: 0, max: 30 },
            { label: 'Between 70 - 100', min: 70, max: 100 }
        ]
    },
};

// Add similar predefined ranges for other filters like change, dayLow, dayHigh, etc.

const UnifiedFilterSystem = ({ onSearch, onClear, onExport, showExport = false, exchanges = [], initialFilters = {}, onFiltersChange, popularScreensButton }) => {
    const theme = useTheme();

    // Flattened list of all available filters
    const [availableFilters, setAvailableFilters] = useState([]);
    // Currently active filters
    const [activeFilters, setActiveFilters] = useState([]);
    // Dialog state
    const [isAddFilterOpen, setIsAddFilterOpen] = useState(false);
    // Search term for filter search
    const [searchTerm, setSearchTerm] = useState('');
    // Set of selected filter IDs
    const [selectedFilters, setSelectedFilters] = useState(new Set());
    // Current tab in the filter dialog
    const [currentTab, setCurrentTab] = useState(0);
    // Lists for sector, industry, country dropdowns
    const industries = useIndustries();
    const sectors = useSectors();
    const countries = useCountries();

    // Initialize available filters based on the filter categories
    useEffect(() => {
        const allFilters = filterCategories.flatMap(category =>
            category.filters.map(filter => {
                if (filter.id === 'exchange') {
                    return { ...filter, options: exchanges };
                }
                if (filter.id === 'sector') {
                    return { ...filter, options: sectors };
                }
                if (filter.id === 'industry') {
                    return { ...filter, options: industries };
                }
                if (filter.id === 'country') {
                    return { ...filter, options: countries };
                }
                return filter;
            })
        );

        setAvailableFilters(allFilters);
    }, [exchanges, sectors, industries, countries]);

    // Apply initial filters from URL parameters
    useEffect(() => {
        if (Object.keys(initialFilters).length > 0) {
            const filtersToApply = [];
            const selectedIds = new Set();

            Object.entries(initialFilters).forEach(([key, value]) => {
                // Handle moving average composite filter
                if (key === 'averageType' || key === 'comparisonType' || key === 'minPercentage' || key === 'maxPercentage') {
                    const existingFilter = filtersToApply.find(f => f.id === 'averageComparison');
                    if (existingFilter) {
                        existingFilter.values[key] = value;
                    } else {
                        const averageFilter = availableFilters.find(f => f.id === 'averageComparison');
                        if (averageFilter) {
                            filtersToApply.push({
                                ...averageFilter,
                                values: {
                                    averageType: initialFilters.averageType || '20',
                                    comparisonType: initialFilters.comparisonType || 'range',
                                    minPercentage: initialFilters.minPercentage || '0',
                                    maxPercentage: initialFilters.maxPercentage || '5',
                                    filterMode: 'range'
                                }
                            });
                            selectedIds.add('averageComparison');
                        }
                    }
                    return;
                }

                // Handle range filters (MoreThan/LowerThan pairs)
                if (key.includes('MoreThan')) {
                    const baseField = key.replace('MoreThan', '');
                    const lowerThanKey = `${baseField}LowerThan`;
                    const filter = availableFilters.find(f => f.id === baseField);

                    if (filter && filter.type === 'range') {
                        filtersToApply.push({
                            ...filter,
                            values: {
                                min: value,
                                max: initialFilters[lowerThanKey] || '',
                                filterMode: 'custom'
                            }
                        });
                        selectedIds.add(baseField);
                    }
                    return;
                }

                // Handle LowerThan keys that don't have corresponding MoreThan keys
                if (key.includes('LowerThan')) {
                    const baseField = key.replace('LowerThan', '');
                    const moreThanKey = `${baseField}MoreThan`;

                    // Only process if there's no corresponding MoreThan key
                    if (!initialFilters[moreThanKey]) {
                        const filter = availableFilters.find(f => f.id === baseField);

                        if (filter && filter.type === 'range') {
                            filtersToApply.push({
                                ...filter,
                                values: {
                                    min: '',
                                    max: value,
                                    filterMode: 'custom'
                                }
                            });
                            selectedIds.add(baseField);
                        }
                    }
                    return;
                }

                // Handle regular filters (select, boolean, etc.)
                const filter = availableFilters.find(f => f.id === key);
                if (filter) {
                    filtersToApply.push({
                        ...filter,
                        values: value
                    });
                    selectedIds.add(key);
                }
            });

            setActiveFilters(filtersToApply);
            setSelectedFilters(selectedIds);
        }
    }, [initialFilters, availableFilters]);

    // Notify parent component when active filters change
    useEffect(() => {
        if (onFiltersChange) {
            onFiltersChange(activeFilters);
        }
    }, [activeFilters, onFiltersChange]);

    // Toggle a filter selection
    const toggleFilter = (filter) => {
        const newSelectedFilters = new Set(selectedFilters);

        if (newSelectedFilters.has(filter.id)) {
            newSelectedFilters.delete(filter.id);
            setActiveFilters(activeFilters.filter(f => f.id !== filter.id));
        } else {
            newSelectedFilters.add(filter.id);
            if (!activeFilters.find(f => f.id === filter.id)) {
                let initialValues;

                if (filter.type === 'composite' && filter.id === 'averageComparison') {
                    initialValues = {
                        averageType: '20',
                        comparisonType: 'range',
                        minPercentage: '0',
                        maxPercentage: '5',
                        filterMode: 'range'
                    };
                } else if (filter.type === 'range') {
                    initialValues = {
                        min: '',
                        max: '',
                        filterMode: 'custom' // Default to custom input
                    };
                } else if (filter.type === 'boolean') {
                    initialValues = false;
                } else {
                    initialValues = '';
                }

                const newFilter = {
                    ...filter,
                    values: initialValues
                };
                setActiveFilters([...activeFilters, newFilter]);
            }
        }

        setSelectedFilters(newSelectedFilters);
    };

    // Handle filter value changes
    const handleFilterValueChange = (filterId, value, field, component = null) => {
        setActiveFilters(activeFilters.map(filter => {
            if (filter.id === filterId) {
                if (filter.type === 'composite') {
                    let newValues = { ...filter.values };

                    if (component === 'comparisonType') {
                        newValues = {
                            ...newValues,
                            [component]: value,
                            minPercentage: '0',
                            maxPercentage: value === 'range' ? '5' : '0'
                        };
                    } else {
                        newValues[component] = value;
                    }

                    return {
                        ...filter,
                        values: newValues
                    };
                } else if (filter.type === 'range') {
                    return {
                        ...filter,
                        values: {
                            ...filter.values,
                            [field]: value
                        }
                    };
                } else {
                    return {
                        ...filter,
                        values: value
                    };
                }
            }
            return filter;
        }));
    };

    // Handle filter mode change (between, above, below, custom)
    const handleFilterModeChange = (filterId, mode, preset = null) => {
        setActiveFilters(activeFilters.map(filter => {
            if (filter.id === filterId) {
                if (filter.type === 'range') {
                    let newValues = { ...filter.values, filterMode: mode };

                    if (preset) {
                        if (mode === 'between') {
                            newValues.min = preset.min;
                            newValues.max = preset.max;
                        } else if (mode === 'above') {
                            newValues.min = preset.value;
                            newValues.max = '';
                        } else if (mode === 'below') {
                            newValues.min = '';
                            newValues.max = preset.value;
                        }
                    } else {
                        // Reset values when changing modes without a preset
                        if (mode === 'custom') {
                            newValues.min = '';
                            newValues.max = '';
                        }
                    }

                    return {
                        ...filter,
                        values: newValues
                    };
                }
            }
            return filter;
        }));
    };

    // Prepare the filter data for submission to the API
    const prepareFilterData = () => {
        return activeFilters.reduce((acc, filter) => {
            if (filter.type === 'range') {
                if (filter.values.min) acc[`${filter.id}MoreThan`] = Number(filter.values.min);
                if (filter.values.max) acc[`${filter.id}LowerThan`] = Number(filter.values.max);
            } else if (filter.type === 'composite' && filter.id === 'averageComparison') {
                acc = {
                    ...acc,
                    averageType: filter.values.averageType,
                    comparisonType: filter.values.comparisonType,
                    minPercentage: filter.values.minPercentage
                };

                if (filter.values.comparisonType === 'range') {
                    acc.maxPercentage = filter.values.maxPercentage;
                }
            } else if (filter.type === 'boolean') {
                acc[filter.id] = filter.values;
            } else {
                acc[filter.id] = filter.values;
            }
            return acc;
        }, {});
    };

    // Handle search button click
    const handleSearch = () => {
        const filterData = prepareFilterData();
        onSearch(filterData);
    };

    // Handle clear button click
    const handleClear = () => {
        setActiveFilters([]);
        setSelectedFilters(new Set());
        onClear();
    };

    // Handle range filter selection UI
    const [filterMenuAnchor, setFilterMenuAnchor] = useState({});

    const openFilterMenu = (event, filterId, mode) => {
        setFilterMenuAnchor({
            ...filterMenuAnchor,
            [filterId]: { element: event.currentTarget, mode }
        });
    };

    const closeFilterMenu = (filterId) => {
        setFilterMenuAnchor({
            ...filterMenuAnchor,
            [filterId]: null
        });
    };

    // Render a filter with Over/Under/Between options
    const renderRangeFilter = (filter) => {
        const ranges = predefinedRanges[filter.id];
        const menuOpen = Boolean(filterMenuAnchor[filter.id]);
        const currentMode = filter.values.filterMode || 'custom';

        return (
            <Stack spacing={1} sx={{ mt: 1 }}>
                {/* Filter Type Selection Buttons */}
                <Stack direction="row" spacing={1}>
                    <Button
                        variant={currentMode === 'above' ? "contained" : "outlined"}
                        size="small"
                        onClick={(e) => openFilterMenu(e, filter.id, 'above')}
                        endIcon={<KeyboardArrowDownIcon />}
                        sx={{ flex: 1 }}
                    >
                        Over
                    </Button>
                    <Button
                        variant={currentMode === 'below' ? "contained" : "outlined"}
                        size="small"
                        onClick={(e) => openFilterMenu(e, filter.id, 'below')}
                        endIcon={<KeyboardArrowDownIcon />}
                        sx={{ flex: 1 }}
                    >
                        Under
                    </Button>
                    <Button
                        variant={currentMode === 'between' ? "contained" : "outlined"}
                        size="small"
                        onClick={(e) => openFilterMenu(e, filter.id, 'between')}
                        endIcon={<KeyboardArrowDownIcon />}
                        sx={{ flex: 1 }}
                    >
                        Between
                    </Button>
                </Stack>

                {/* "Above" Menu */}
                <Menu
                    anchorEl={filterMenuAnchor[filter.id]?.element}
                    open={menuOpen && filterMenuAnchor[filter.id]?.mode === 'above'}
                    onClose={() => closeFilterMenu(filter.id)}
                    PaperProps={{
                        sx: {
                            mt: 1,
                            width: 200,
                            maxHeight: 300,
                            backgroundColor: theme.palette.background.paper
                        }
                    }}
                >
                    {ranges && ranges.above ? (
                        ranges.above.map((range) => (
                            <MenuItem
                                key={range.label}
                                onClick={() => {
                                    handleFilterModeChange(filter.id, 'above', range);
                                    closeFilterMenu(filter.id);
                                }}
                                sx={{
                                    color: theme.palette.text.primary,
                                    '&:hover': { bgcolor: theme.palette.action.hover }
                                }}
                            >
                                {range.label}
                            </MenuItem>
                        ))
                    ) : (
                        <MenuItem disabled>No preset values available</MenuItem>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <MenuItem
                        onClick={() => {
                            handleFilterModeChange(filter.id, 'custom');
                            closeFilterMenu(filter.id);
                        }}
                        sx={{
                            color: theme.palette.text.primary,
                            '&:hover': { bgcolor: theme.palette.action.hover }
                        }}
                    >
                        Custom Value
                    </MenuItem>
                </Menu>

                {/* "Below" Menu */}
                <Menu
                    anchorEl={filterMenuAnchor[filter.id]?.element}
                    open={menuOpen && filterMenuAnchor[filter.id]?.mode === 'below'}
                    onClose={() => closeFilterMenu(filter.id)}
                    PaperProps={{
                        sx: {
                            mt: 1,
                            width: 200,
                            maxHeight: 300,
                            backgroundColor: theme.palette.background.paper
                        }
                    }}
                >
                    {ranges && ranges.below ? (
                        ranges.below.map((range) => (
                            <MenuItem
                                key={range.label}
                                onClick={() => {
                                    handleFilterModeChange(filter.id, 'below', range);
                                    closeFilterMenu(filter.id);
                                }}
                                sx={{
                                    color: theme.palette.text.primary,
                                    '&:hover': { bgcolor: theme.palette.action.hover }
                                }}
                            >
                                {range.label}
                            </MenuItem>
                        ))
                    ) : (
                        <MenuItem disabled>No preset values available</MenuItem>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <MenuItem
                        onClick={() => {
                            handleFilterModeChange(filter.id, 'custom');
                            closeFilterMenu(filter.id);
                        }}
                        sx={{
                            color: theme.palette.text.primary,
                            '&:hover': { bgcolor: theme.palette.action.hover }
                        }}
                    >
                        Custom Value
                    </MenuItem>
                </Menu>

                {/* "Between" Menu */}
                <Menu
                    anchorEl={filterMenuAnchor[filter.id]?.element}
                    open={menuOpen && filterMenuAnchor[filter.id]?.mode === 'between'}
                    onClose={() => closeFilterMenu(filter.id)}
                    PaperProps={{
                        sx: {
                            mt: 1,
                            width: 200,
                            maxHeight: 300,
                            backgroundColor: theme.palette.background.paper
                        }
                    }}
                >
                    {ranges && ranges.between ? (
                        ranges.between.map((range) => (
                            <MenuItem
                                key={range.label}
                                onClick={() => {
                                    handleFilterModeChange(filter.id, 'between', range);
                                    closeFilterMenu(filter.id);
                                }}
                                sx={{
                                    color: theme.palette.text.primary,
                                    '&:hover': { bgcolor: theme.palette.action.hover }
                                }}
                            >
                                {range.label}
                            </MenuItem>
                        ))
                    ) : (
                        <MenuItem disabled>No preset values available</MenuItem>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <MenuItem
                        onClick={() => {
                            handleFilterModeChange(filter.id, 'custom');
                            closeFilterMenu(filter.id);
                        }}
                        sx={{
                            color: theme.palette.text.primary,
                            '&:hover': { bgcolor: theme.palette.action.hover }
                        }}
                    >
                        Custom Values
                    </MenuItem>
                </Menu>

                {/* Input Fields for Custom Values */}
                {currentMode === 'custom' && (
                    <Stack direction="row" spacing={1}>
                        <TextField
                            size="small"
                            placeholder="Min"
                            value={filter.values.min || ''}
                            onChange={(e) => handleFilterValueChange(filter.id, e.target.value, 'min')}
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: theme.palette.text.primary,
                                    '& fieldset': {
                                        borderColor: theme.palette.mode === 'dark'
                                            ? theme.palette.text.secondary
                                            : theme.palette.text.disabled,
                                    },
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: theme.palette.text.secondary,
                                },
                            }}
                        />
                        <TextField
                            size="small"
                            placeholder="Max"
                            value={filter.values.max || ''}
                            onChange={(e) => handleFilterValueChange(filter.id, e.target.value, 'max')}
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: theme.palette.text.primary,
                                    '& fieldset': {
                                        borderColor: theme.palette.mode === 'dark'
                                            ? theme.palette.text.secondary
                                            : theme.palette.text.disabled,
                                    },
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: theme.palette.text.secondary,
                                },
                            }}
                        />
                    </Stack>
                )}

                {/* Display Current Filter Selection */}
                {currentMode !== 'custom' && (
                    <Box sx={{ mt: 1 }}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1,
                                backgroundColor: theme.palette.background.paper,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}
                        >
                            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                {currentMode === 'above' && filter.values.min ? `Above ${filter.values.min}` :
                                    currentMode === 'below' && filter.values.max ? `Under ${filter.values.max}` :
                                        currentMode === 'between' && filter.values.min && filter.values.max ?
                                            `Between ${filter.values.min} - ${filter.values.max}` : 'Select a value'}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => handleFilterModeChange(filter.id, 'custom')}
                                sx={{ color: theme.palette.text.secondary }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Paper>
                    </Box>
                )}
            </Stack>
        );
    };

    // Render a composite filter (like Moving Average comparison)
    const renderCompositeFilter = (filter) => {
        const { averageType, comparisonType, minPercentage, maxPercentage } = filter.values;

        const getFilterSummary = () => {
            if (!averageType || !comparisonType) return null;

            const avgType = averageType === '20' ? '20-Day Average' :
                averageType === '50' ? '50-Day Average' :
                    averageType === '100' ? '100-Day Average' :
                        averageType === '150' ? '150-Day Average' :
                            averageType === '200' ? '200-Day Average' : 'Unknown Average';

            if (comparisonType === 'range') {
                return `${avgType}: Between ${minPercentage || 0}% and ${maxPercentage || 0}%`;
            } else if (comparisonType === 'above') {
                return `${avgType}: Above ${minPercentage || 0}%`;
            } else if (comparisonType === 'below') {
                return `${avgType}: Below ${minPercentage || 0}%`;
            }

            return null;
        };

        const filterSummary = getFilterSummary();

        return (
            <Stack spacing={1} sx={{ mt: 1 }}>
                <FormControl fullWidth size="small">
                    <Select
                        value={averageType || '50'}
                        onChange={(e) => handleFilterValueChange(filter.id, e.target.value, null, 'averageType')}
                        sx={{
                            color: theme.palette.text.primary,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.mode === 'dark'
                                    ? theme.palette.text.secondary
                                    : theme.palette.text.disabled,
                            },
                            '& .MuiSvgIcon-root': {
                                color: theme.palette.text.primary,
                            },
                            '& .MuiSelect-select': {
                                color: theme.palette.text.primary,
                            },
                        }}
                    >
                        <MenuItem value="20" sx={{ color: theme.palette.text.primary }}>20-Day Average</MenuItem>
                        <MenuItem value="50" sx={{ color: theme.palette.text.primary }}>50-Day Average</MenuItem>
                        <MenuItem value="100" sx={{ color: theme.palette.text.primary }}>100-Day Average</MenuItem>
                        <MenuItem value="150" sx={{ color: theme.palette.text.primary }}>150-Day Average</MenuItem>
                        <MenuItem value="200" sx={{ color: theme.palette.text.primary }}>200-Day Average</MenuItem>
                    </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                    <Select
                        value={comparisonType || 'range'}
                        onChange={(e) => handleFilterValueChange(filter.id, e.target.value, null, 'comparisonType')}
                        sx={{
                            color: theme.palette.text.primary,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.mode === 'dark'
                                    ? theme.palette.text.secondary
                                    : theme.palette.text.disabled,
                            },
                            '& .MuiSvgIcon-root': {
                                color: theme.palette.text.primary,
                            },
                            '& .MuiSelect-select': {
                                color: theme.palette.text.primary,
                            },
                        }}
                    >
                        <MenuItem value="range" sx={{ color: theme.palette.text.primary }}>Within Range</MenuItem>
                        <MenuItem value="above" sx={{ color: theme.palette.text.primary }}>Above</MenuItem>
                        <MenuItem value="below" sx={{ color: theme.palette.text.primary }}>Below</MenuItem>
                    </Select>
                </FormControl>

                {comparisonType === 'range' ? (
                    <>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 1 }}>
                            Percentage Range
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <TextField
                                size="small"
                                placeholder="Min %"
                                value={minPercentage || '0'}
                                onChange={(e) => handleFilterValueChange(filter.id, e.target.value, null, 'minPercentage')}
                                fullWidth
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme.palette.text.primary,
                                        '& fieldset': {
                                            borderColor: theme.palette.mode === 'dark'
                                                ? theme.palette.text.secondary
                                                : theme.palette.text.disabled,
                                        },
                                    },
                                }}
                            />
                            <TextField
                                size="small"
                                placeholder="Max %"
                                value={maxPercentage || '5'}
                                onChange={(e) => handleFilterValueChange(filter.id, e.target.value, null, 'maxPercentage')}
                                fullWidth
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme.palette.text.primary,
                                        '& fieldset': {
                                            borderColor: theme.palette.mode === 'dark'
                                                ? theme.palette.text.secondary
                                                : theme.palette.text.disabled,
                                        },
                                    },
                                }}
                            />
                        </Stack>
                    </>
                ) : (
                    <>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 1 }}>
                            Percentage
                        </Typography>
                        <TextField
                            size="small"
                            placeholder="Percentage"
                            value={minPercentage || '0'}
                            onChange={(e) => handleFilterValueChange(filter.id, e.target.value, null, 'minPercentage')}
                            fullWidth
                            InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: theme.palette.text.primary,
                                    '& fieldset': {
                                        borderColor: theme.palette.mode === 'dark'
                                            ? theme.palette.text.secondary
                                            : theme.palette.text.disabled,
                                    },
                                },
                            }}
                        />
                    </>
                )}

                {filterSummary && (
                    <Box sx={{ mt: 1 }}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1,
                                backgroundColor: theme.palette.background.paper,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}
                        >
                            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                {filterSummary}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    handleFilterValueChange(filter.id, '20', null, 'averageType');
                                    handleFilterValueChange(filter.id, 'range', null, 'comparisonType');
                                    handleFilterValueChange(filter.id, '0', null, 'minPercentage');
                                    handleFilterValueChange(filter.id, '5', null, 'maxPercentage');
                                }}
                                sx={{ color: theme.palette.text.secondary }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Paper>
                    </Box>
                )}
            </Stack>
        );
    };

    const renderSelectFilter = (filter) => {
        // Special handling for Exchange filter to make it multi-select
        if (filter.id === 'exchange') {
            return (
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                    <Select
                        multiple
                        value={filter.values || []}
                        onChange={(e) => handleFilterValueChange(filter.id, e.target.value)}
                        displayEmpty
                        renderValue={(selected) => {
                            if (selected.length === 0) {
                                return <em style={{ opacity: 0.7 }}>Select exchanges</em>;
                            }
                            return selected.join(', ');
                        }}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    maxHeight: '300px',
                                    backgroundColor: theme.palette.background.paper,
                                    '& .MuiMenuItem-root': {
                                        color: theme.palette.text.primary,
                                    },
                                },
                            },
                        }}
                        sx={{
                            color: theme.palette.text.primary,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.mode === 'dark'
                                    ? theme.palette.text.secondary
                                    : theme.palette.text.disabled,
                            },
                            '& .MuiSvgIcon-root': {
                                color: theme.palette.text.primary,
                            },
                            '& .MuiSelect-select': {
                                color: theme.palette.text.primary,
                            },
                        }}
                    >
                        {filter.options?.map((option) => (
                            <MenuItem
                                key={option}
                                value={option}
                                sx={{
                                    color: theme.palette.text.primary,
                                    '&:hover': {
                                        bgcolor: theme.palette.action.hover
                                    }
                                }}
                            >
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
        }

        // Regular select for other filters
        return (
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                <Select
                    value={filter.values || ''}
                    onChange={(e) => handleFilterValueChange(filter.id, e.target.value)}
                    displayEmpty
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                maxHeight: '200px',
                                backgroundColor: theme.palette.background.paper,
                                '& .MuiMenuItem-root': {
                                    color: theme.palette.text.primary,
                                },
                            },
                        },
                    }}
                    sx={{
                        color: theme.palette.text.primary,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.mode === 'dark'
                                ? theme.palette.text.secondary
                                : theme.palette.text.disabled,
                        },
                        '& .MuiSvgIcon-root': {
                            color: theme.palette.text.primary,
                        },
                        '& .MuiSelect-select': {
                            color: theme.palette.text.primary,
                        },
                    }}
                >
                    <MenuItem value="" sx={{
                        color: theme.palette.text.primary,
                        opacity: 0.7
                    }}>
                        Select {filter.label}
                    </MenuItem>
                    {filter.options?.map((option) => (
                        <MenuItem
                            key={option}
                            value={option}
                            sx={{
                                color: theme.palette.text.primary,
                                '&:hover': {
                                    bgcolor: theme.palette.action.hover
                                }
                            }}
                        >
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    };

    // Render a boolean filter
    const renderBooleanFilter = (filter) => {
        return (
            <Box sx={{ mt: 1 }}>
                <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        variant={filter.values === true ? "contained" : "outlined"}
                        onClick={() => handleFilterValueChange(filter.id, true)}
                        fullWidth
                    >
                        Yes
                    </Button>
                    <Button
                        size="small"
                        variant={filter.values === false ? "contained" : "outlined"}
                        onClick={() => handleFilterValueChange(filter.id, false)}
                        fullWidth
                    >
                        No
                    </Button>
                </Stack>
            </Box>
        );
    };

    // Render the appropriate filter content based on type
    const renderFilterContent = (filter) => {
        if (filter.type === 'range') {
            return renderRangeFilter(filter);
        }

        if (filter.type === 'composite') {
            return renderCompositeFilter(filter);
        }

        if (filter.type === 'select') {
            return renderSelectFilter(filter);
        }

        if (filter.type === 'boolean') {
            return renderBooleanFilter(filter);
        }
    };

    // Filter options based on search term
    const filteredOptions = availableFilters.filter(filter =>
        filter.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Box sx={{ mb: 3 }}>
            <Paper
                variant='stats'
                sx={{
                    p: 2,
                    borderRadius: 1
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{
                        color: theme.palette.text.primary
                    }}>
                        {activeFilters.length} Filters
                    </Typography>
                    {popularScreensButton}
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setIsAddFilterOpen(true)}
                        variant="contained"
                    >
                        Add Filters
                    </Button>
                </Stack>

                {activeFilters.length > 0 && (
                    <Grid container spacing={2}>
                        {activeFilters.map((filter) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={filter.id}>
                                <Paper
                                    variant='stats'
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 1
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Typography variant="subtitle2" sx={{
                                            flex: 1,
                                            color: theme.palette.text.primary
                                        }}>
                                            {filter.label}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleFilter(filter)}
                                            sx={{ color: theme.palette.text.primary }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                    {renderFilterContent(filter)}
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                )}

                <Grid container spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                    {showExport && (
                        <Grid item>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<FileDownloadIcon />}
                                onClick={onExport}
                            >
                                Export
                            </Button>
                        </Grid>
                    )}
                    <Grid item>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleClear}
                        >
                            Clear
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={handleSearch}
                            startIcon={<SearchIcon />}
                        >
                            Search
                        </Button>
                    </Grid>
                </Grid>

                <Dialog
                    open={isAddFilterOpen}
                    onClose={() => setIsAddFilterOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    sx={{
                        '& .MuiDialog-container': {
                            backdropFilter: 'blur(4px)',
                        },
                        '& .MuiPaper-root': {
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(13, 34, 55, 0.95)'
                                : 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(8px)',
                            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
                        }
                    }}
                >
                    <DialogTitle
                        sx={{
                            backgroundColor: 'transparent',
                            borderBottom: `1px solid ${theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(0, 0, 0, 0.1)'}`,
                            padding: '16px 24px'
                        }}
                    >
                        Add Filters
                        <IconButton
                            onClick={() => setIsAddFilterOpen(false)}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                color: theme.palette.text.secondary,
                                '&:hover': {
                                    color: theme.palette.text.primary,
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent
                        sx={{
                            backgroundColor: 'transparent',
                            padding: '20px 24px',
                        }}
                    >
                        <TextField
                            fullWidth
                            placeholder="Search filters..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    color: theme.palette.text.primary,
                                    '& fieldset': {
                                        borderColor: theme.palette.mode === 'dark'
                                            ? theme.palette.text.secondary
                                            : theme.palette.text.disabled,
                                    },
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: theme.palette.text.secondary,
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Tabs
                            value={currentTab}
                            onChange={(e, newValue) => setCurrentTab(newValue)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                mb: 2,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                '& .MuiTab-root': {
                                    color: theme.palette.text.secondary,
                                    '&.Mui-selected': {
                                        color: theme.palette.primary.main,
                                    }
                                }
                            }}
                        >
                            <Tab label="All" sx={{ textTransform: 'none' }} />
                            {filterCategories.map((category) => (
                                <Tab
                                    key={category.id}
                                    label={category.label}
                                    sx={{ textTransform: 'none' }}
                                />
                            ))}
                        </Tabs>

                        <List sx={{ width: '100%' }}>
                            {(currentTab === 0 ? filteredOptions :
                                filteredOptions.filter(filter =>
                                    filterCategories[currentTab - 1]?.filters.some(f => f.id === filter.id)
                                )
                            ).map((filter) => (
                                <ListItem
                                    key={filter.id}
                                    button
                                    onClick={() => toggleFilter(filter)}
                                    sx={{
                                        borderRadius: 1,
                                        mb: 0.5,
                                        color: theme.palette.text.primary,
                                        '&:hover': {
                                            bgcolor: theme.palette.action.hover
                                        }
                                    }}
                                >
                                    <ListItemText primary={filter.label} />
                                    {selectedFilters.has(filter.id) && (
                                        <ListItemIcon sx={{ minWidth: 'auto' }}>
                                            <CheckCircleIcon color="success" />
                                        </ListItemIcon>
                                    )}
                                </ListItem>
                            ))}
                        </List>
                    </DialogContent>

                    <DialogActions
                        sx={{
                            backgroundColor: 'transparent',
                            borderTop: `1px solid ${theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(0, 0, 0, 0.1)'}`,
                            padding: '16px 24px',
                        }}
                    >
                        <Button
                            onClick={() => setIsAddFilterOpen(false)}
                            variant="contained"
                        >
                            Apply Filters
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </Box>
    );
};

export default UnifiedFilterSystem;